import { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Spinner } from 'common/spinner';
import { Button, ButtonStyle } from 'common/button';
import { AppContext } from 'context/app-context';
import { LocalStorageApi } from 'helpers/local-storage';
import { goToPage, RouteName } from 'routes';
import { getRouteFor } from 'routes/route-helpers';
import { AlbumInviteController } from 'networking/controllers/album-invite-controller';
import styles from './album-invite.module.scss';

// Public accept/decline landing page for an album invitation (reached from the
// invitation email link: /albums/invite/:albumId?token=...).
export const AlbumInvite = () => {
  useParams<{ albumId: string }>();
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const { state } = useContext(AppContext);
  const [info, setInfo] = useState<AlbumInviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
      setLoading(false);
      return;
    }
    AlbumInviteController.getAlbumInviteInfo(token)
      .then(setInfo)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  // The member's album screen. Signed out, they sign in first (with the
  // invited email) and land there.
  const openAlbum = ({ vaultId, albumId }: AlbumInviteInfo) => {
    const albumScreen = { id: vaultId, albumId };
    if (state.logged) {
      goToPage(RouteName.AlbumScreen, albumScreen);
      return;
    }
    LocalStorageApi.set(
      'previous-url',
      getRouteFor(RouteName.AlbumScreen, albumScreen),
    );
    goToPage(RouteName.SignIn);
  };

  const handleAccept = async () => {
    setActing(true);
    try {
      const updated = await AlbumInviteController.acceptAlbumInvite(token);
      openAlbum(updated);
    } catch (e) {
      setError(true);
    } finally {
      setActing(false);
    }
  };

  const handleDecline = async () => {
    setActing(true);
    try {
      setInfo(await AlbumInviteController.declineAlbumInvite(token));
    } catch (e) {
      setError(true);
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Invitation not found</h1>
          <p className={styles.text}>
            This invitation link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  const roleVerb =
    { viewer: 'view', editor: 'edit' }[info.role as string] || 'contribute to';

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {info.status === 'active' && (
          <>
            <h1 className={styles.title}>You&apos;re in!</h1>
            <p className={styles.text}>
              You&apos;ve joined <strong>{info.albumName}</strong> as a{' '}
              {info.role}.
            </p>
            <Button
              buttonStyle={ButtonStyle.Primary}
              onClick={() => openAlbum(info)}
            >
              Open album
            </Button>
          </>
        )}

        {info.status === 'declined' && (
          <>
            <h1 className={styles.title}>Invitation declined</h1>
            <p className={styles.text}>
              You&apos;ve declined the invitation to{' '}
              <strong>{info.albumName}</strong>.
            </p>
          </>
        )}

        {info.status === 'invited' && (
          <>
            <h1 className={styles.title}>
              {info.inviterName || 'Someone'} invited you
            </h1>
            <p className={styles.text}>
              to {roleVerb} the album <strong>{info.albumName}</strong> on
              Generational Story.
            </p>
            <div className={styles.actions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                disabled={acting}
                onClick={handleDecline}
              >
                Decline
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                disabled={acting}
                onClick={handleAccept}
              >
                Accept
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
